// The customer portal's API (Clients, Phase 5), exactly as deleg8/docs/PORTAL.md says (server/src/portal.ts): /api/v1/portal/*.
// Answers are built for the customer: requests, their words, a customer status, and the updates the owner published.
import type { FileRef } from './files';

export type Urgency = 'low' | 'normal' | 'high' | 'urgent';
export type PortalStatus = 'received' | 'in_progress' | 'waiting' | 'done' | 'closed';
export interface PortalRequest {
  id: string;
  number: number;
  client: { id: string; name: string };
  board: { id: string; name: string };
  title: string;
  details: string;
  urgency: Urgency;
  status: PortalStatus;
  statusLabel: string;
  createdBy: { name: string; you: boolean };
  createdAt: number;
  updatedAt: number;
  closedAt: number | null;
  updates: number;
  /** Photos and files sent with the request (docs/FILES.md). */
  files: FileRef[];
}
export interface PortalMessage { id: string; kind: 'update' | 'reply'; fromCustomer: boolean; author: { name: string; /** True only for the reader's own words (the portal routes send it; the team's thread and the demo leave it out). */ you?: boolean }; text: string; at: number; files: FileRef[] }
export interface PortalMe {
  person: { id: string; name: string | null; email: string | null };
  csrfToken: string | null;
  /** The Clients this person is a customer of, each with its boards that are shown in the portal (docs/PORTAL.md). */
  clients: { id: string; name: string; owner: { name: string }; role: 'admin' | 'requester' | 'viewer'; ownRequestsOnly: boolean; boards: { id: string; name: string }[] }[];
}

export const STATUS_HINT: Record<PortalStatus, string> = {
  received: 'We have it and will start soon.',
  in_progress: 'We are working on it.',
  waiting: 'We need something from you. Reply below.',
  done: 'This is finished. Close it when you are happy.',
  closed: 'This request is closed. Send a new request if you need more.',
};

/** Eli's side (the owner and managers): a client list's ticket, its customer thread with drafts, and who is invited. */
export interface TeamMessage extends PortalMessage { state: 'draft' | 'published'; waiting: boolean; createdAt: number; updatedAt: number; publishedAt: number | null }
export interface TicketCustomer { request: PortalRequest | null; thread: TeamMessage[] }
export interface ClientPeople { members: { id: string; name: string | null; email: string | null; at: number }[]; invites: { email: string; at: number; expiresAt: number }[] }

/** Every shown board across the person's Clients, with the Client it belongs to. */
export const boardsOf = (me: PortalMe) => me.clients.flatMap((c) => c.boards.map((b) => ({ ...b, client: { id: c.id, name: c.name, role: c.role } })));
/** A viewer reads requests and updates but cannot send, reply or close (docs/USERS.md). */
export const canSendIn = (me: PortalMe, clientId: string) => (me.clients.find((c) => c.id === clientId)?.role ?? 'requester') !== 'viewer';
/** A client admin closes any of their Client's requests; a requester only their own (docs/USERS.md, portal.requests.close). */
export const canCloseIn = (me: PortalMe, r: PortalRequest): boolean => { const role = me.clients.find((c) => c.id === r.client.id)?.role; return role === 'admin' || (role === 'requester' && r.createdBy.you === true); };
/** The list's name: "My requests" only when the person sees just their own (a requester with own-only on), else "Requests". */
export const homeLabel = (me: PortalMe) => (me.clients.length && me.clients.every((c) => c.role === 'requester' && c.ownRequestsOnly) ? 'My requests' : 'Requests');
/** The line under a request: what it waits on, in words for what this reader can do (a viewer cannot reply or close). */
export function statusHint(me: PortalMe, r: PortalRequest): string {
  const acts = canSendIn(me, r.client.id);
  if (r.status === 'waiting' && !acts) return r.createdBy.you ? 'We need something from you.' : `We need something from ${r.createdBy.name}.`;
  if (r.status === 'waiting' && !r.createdBy.you) return `We need something from ${r.createdBy.name}. Reply below.`;
  if (r.status === 'done' && !canCloseIn(me, r)) return 'This is finished.';
  if (r.status === 'closed' && !acts) return 'This request is closed.';
  return STATUS_HINT[r.status];
}
/** The reader wrote this reply: the server's `author.you`; without it (the demo), a client reply under the reader's own name. */
export const byYou = (me: PortalMe, m: PortalMessage) => m.author.you ?? (m.fromCustomer && !!me.person.name && m.author.name === me.person.name);
